import axios from 'axios';

const apiPrefix = import.meta.env.VITE_API_PREFIX || '/openapi';

export const http = axios.create({
  baseURL: apiPrefix,
  timeout: 120000,
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('assets_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (res) => {
    const body = res.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (!body.success) {
        return Promise.reject(new Error(body.message || '请求失败'));
      }
      return body.data;
    }
    return body;
  },
  (err) => {
    const msg = err?.response?.data?.message || err?.message || '网络异常';
    if (err?.response?.status === 401) {
      localStorage.removeItem('assets_admin_token');
      localStorage.removeItem('assets_admin_user');
      if (!location.pathname.includes('/login')) {
        location.href = '/login';
      }
    }
    return Promise.reject(new Error(msg));
  },
);

export type PageResult<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};

export type ImageItem = {
  id: number;
  resource_id: string;
  name: string;
  description: string;
  tags: string;
  file_url: string;
  url_expires_at?: string;
  mime_type: string;
  file_size: number;
  width: number;
  height: number;
  format: string;
  status: string;
  updated_at: string;
};

export type VideoItem = {
  id: number;
  resource_id: string;
  name: string;
  description: string;
  tags: string;
  file_url: string;
  url_expires_at?: string;
  mime_type: string;
  file_size: number;
  width: number;
  height: number;
  duration_ms: number;
  fps: number;
  bitrate: number;
  status: string;
  updated_at: string;
};

export type SfxItem = {
  id: number;
  resource_id: string;
  name: string;
  description: string;
  tags: string;
  category: string;
  file_url: string;
  url_expires_at?: string;
  mime_type: string;
  file_size: number;
  duration_ms: number;
  status: string;
  updated_at: string;
};

export type BgmItem = {
  id: number;
  resource_id: string;
  name: string;
  description: string;
  tags: string;
  file_url: string;
  url_expires_at?: string;
  mime_type: string;
  file_size: number;
  duration_ms: number;
  status: string;
  updated_at: string;
};

export type Stats = {
  total: number;
  by_type: Record<string, number>;
  total_size: number;
  clients: number;
  images: { total: number; active: number; total_size: number };
  videos: { total: number; active: number; total_size: number };
  sfx: { total: number; active: number; total_size: number };
  bgm: { total: number; active: number; total_size: number };
};

export type ApiClient = {
  id: number;
  name: string;
  access_key: string;
  secret_key?: string;
  allowed_types: string;
  status: string;
  rate_limit: number;
  remark: string;
  created_at: string;
  updated_at: string;
};

function crud<T>(base: string) {
  return {
    list: (params: Record<string, unknown>) =>
      http.get(base, { params }) as Promise<PageResult<T>>,
    create: (form: FormData) =>
      http.post(base, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }) as Promise<T>,
    update: (id: number, data: FormData | Record<string, unknown>) => {
      if (data instanceof FormData) {
        return http.put(`${base}/${id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }) as Promise<T>;
      }
      return http.put(`${base}/${id}`, data) as Promise<T>;
    },
    remove: (id: number) => http.delete(`${base}/${id}`),
  };
}

export const api = {
  login: (username: string, password: string) =>
    http.post('/auth/login', { username, password }) as Promise<{
      token: string;
      username: string;
      expires_in: number;
    }>,
  me: () => http.get('/auth/me') as Promise<{ id: number; username: string }>,
  stats: () => http.get('/admin/stats') as Promise<Stats>,
  images: crud<ImageItem>('/admin/images'),
  videos: crud<VideoItem>('/admin/videos'),
  sfx: crud<SfxItem>('/admin/sfx'),
  bgm: crud<BgmItem>('/admin/bgm'),
  listClients: (params?: Record<string, unknown>) =>
    http.get('/admin/clients', { params }) as Promise<{ items: ApiClient[]; total: number }>,
  createClient: (data: Record<string, unknown>) =>
    http.post('/admin/clients', data) as Promise<ApiClient>,
  updateClient: (id: number, data: Record<string, unknown>) =>
    http.put(`/admin/clients/${id}`, data) as Promise<ApiClient>,
  deleteClient: (id: number) => http.delete(`/admin/clients/${id}`),
};

export function formatBytes(n: number) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatDuration(ms: number) {
  if (!ms) return '-';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}
